import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Button,
    Container,
    useMediaQuery,
    createTheme,
    ThemeProvider,
    Skeleton,
    Stack,
    Autocomplete,
    TextField,
    Drawer,
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Collapse,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import playFill from '@iconify/icons-eva/play-circle-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import { CheckCircle, Cancel, Edit as EditIcon, ExpandMore, Delete as DeleteIcon, ToggleOn, ToggleOff, PlayArrow, History, Visibility, AutoAwesome, KeyboardArrowDown, KeyboardArrowUp, Add as AddIcon, Replay } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
// Tabs replaced with ToggleButtonGroup for cleaner UX
import { debounce } from 'lodash';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';

import Page from 'src/components/Page';
import { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import UserInfo from 'src/utils/Authorization/UserInfo';
import Tooltip from '@mui/material/Tooltip';
import AddCompany from '../company/AddCompany';
import EnrichmentProgress from './EnrichmentProgress';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                paper: {
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                },
                responsiveBase: {
                    flex: 1,
                    overflow: 'auto',
                }
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    backgroundColor: '#f8f9fa',
                    padding: '10px 8px',
                    '&:first-of-type': {
                        paddingLeft: '16px',
                    },
                },
                data: {
                    fontWeight: '600',
                    fontSize: '12px',
                    color: '#637381',
                },
                sortAction: {
                    display: 'inline-flex',
                    alignItems: 'center',
                },
                sortActive: {
                    color: '#212B36',
                },
                toolButton: {
                    fontWeight: '600',
                    fontSize: '12px',
                }
            }
        },
        MUIDataTableBodyCell: {
            styleOverrides: {
                root: {
                    padding: '10px 8px',
                    fontSize: '13px',
                    color: '#212B36',
                    '&:first-of-type': {
                        paddingLeft: '16px',
                    },
                }
            }
        },
        MUIDataTableToolbar: {
            styleOverrides: {
                root: {
                    display: 'none',
                }
            }
        },
        MUIDataTableFooter: {
            styleOverrides: {
                root: {
                    borderTop: '1px solid #e0e0e0',
                }
            }
        }
    },
    typography: {
        fontFamily: "Lexend"
    }
});

const StyledButton = {
    backgroundColor: "#00AB55",
    fontFamily: "Lexend,Public Sans,sans-serif",
    fontWeight: "600",
    borderRadius: "8px",
    boxShadow: "0 4px 12px 0 rgb(0 171 85 / 15%)",
    textTransform: 'none',
    padding: '8px 20px',
};

// Main tabs - 3 tabs: Outreach, Prompt, Pitch (Outreach first)
const MAIN_TABS = [
    { value: 'outreach', label: 'Outreach' },
    { value: 'prompt', label: 'Prompt' },
    { value: 'pitch', label: 'Pitch' },
];

const DEFAULT_PROMPT_EXAMPLE = `Generate a personalized outreach pitch for this company on behalf of TGC Capital Partners, the strategic investment arm of Gateway Group of Companies.

**About TGC Capital Partners:**
- We are a hands-on investment partner with 25+ years of cross-industry experience
- We focus on tech-driven, founder-led companies poised for accelerated growth
- We are industry agnostic but prioritize companies where technology is a key success factor
- We offer more than capital - we provide operational, technical, and strategic support

**Our Value Proposition (The 6X Factors):**
1. Tech Excellence - Access to world-class technical expertise and global delivery model
2. Marketing, Lead Gen & Global Sales Teams - Help establish global go-to-market engines
3. Governance - Strategic mentorship and steering committee participation
4. Money for Growth - We invest 20-40% of optimized funding needs
5. Mentoring - Hands-on guidance from experienced operators
6. Internationalization & Global Reach - Access to networks across 12+ countries

**Key Benefits We Deliver:**
- 30-40% lower scaling costs through our global delivery model
- 30-40% funding need optimization
- Access to Gateway's infrastructure, branding, and marketing resources

**Pitch Guidelines:**
- Keep the tone professional yet warm and partnership-oriented
- Highlight how we can add specific value based on the company's industry/stage
- Emphasize we seek alignment in vision and values, not just financial returns
- Focus on minority stakes with significant hands-on involvement
- Mention our typical 4-8 week deal timeline from first call to proposal
- Do NOT include generic investment jargon - be specific and genuine
- End with a clear, low-pressure call to action for an introductory conversation

**Company Information to Analyze:**
Review the company details below and identify:
- Their likely growth stage (startup, scaleup, or mature)
- Key areas where TGC's expertise could accelerate their growth
- Specific value propositions most relevant to their business

Generate a concise, compelling pitch (under 1000 characters) that feels personalized and demonstrates genuine understanding of their business.`;

// --- Add/Edit Pitch Form Component (No Country) ---
const PromptForm = ({ id, onClose, onSuccess, viewMode = false }) => {
    const [initialValues, setInitialValues] = useState({
        title: '',
        prompt_text: ''
    });
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (id) {
            apiInstance.get(`Prompt-templates/${id}/`)
                .then((response) => {
                    const data = response?.data?.data;
                    if (data) {
                        setInitialValues({
                            title: data.title || '',
                            prompt_text: data.prompt_text
                        });
                    }
                })
                .catch((error) => {
                    showErrorToast(error);
                    onClose();
                });
        }
    }, [id, onClose]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        prompt_text: Yup.string().required("Pitch text is required")
    });

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            const payload = {
                title: values.title,
                prompt_text: values.prompt_text
            };

            const method = id ? 'put' : 'post';
            const url = id ? `Prompt-templates/${id}/` : 'Prompt-templates/';

            apiInstance[method](url, payload)
                .then((response) => {
                    showToast(response?.data?.message || 'Pitch saved successfully');
                    onSuccess();
                    onClose();
                })
                .catch((error) => {
                    showErrorToast(error);
                    setSubmitting(false);
                });
        }
    });

    const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

    const handleGeneratePrompt = async () => {
        if (!values.prompt_text?.trim()) {
            showErrorToast({ message: 'Please enter a description first' });
            return;
        }
        
        setIsGenerating(true);
        try {
            const response = await apiInstance.post('Prompt-templates/generate-prompt/', {
                description: values.prompt_text
            });
            
            if (response?.data?.success && response?.data?.data?.generated_prompt) {
                setFieldValue('prompt_text', response.data.data.generated_prompt);
                showToast('Prompt generated successfully!');
            } else {
                showErrorToast({ message: response?.data?.message || 'Failed to generate prompt' });
            }
        } catch (error) {
            showErrorToast(error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h6">{viewMode ? "View Pitch" : (id ? "Edit Pitch" : "New Pitch")}</Typography>
                <IconButton onClick={onClose}>
                    <Icon icon={closeFill} width={24} height={24} />
                </IconButton>
            </Stack>

            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={3} sx={{ flexGrow: 1, overflowY: 'auto', pb: 2 }}>
                        <TextField
                            fullWidth
                            label="Title"
                            {...getFieldProps('title')}
                            error={Boolean(touched.title && errors.title)}
                            helperText={touched.title && errors.title}
                            placeholder="Enter a title to identify this pitch"
                            disabled={viewMode}
                            InputProps={{ readOnly: viewMode }}
                        />

                        <Accordion sx={{ mb: 1 }}>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="prompt-example-content"
                                id="prompt-example-header"
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    📋 Reference: Default Prompt Example
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        maxHeight: 400,
                                        overflowY: 'auto'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.6,
                                            color: 'text.secondary',
                                            m: 0
                                        }}
                                    >
                                        {DEFAULT_PROMPT_EXAMPLE}
                                    </Typography>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Prompt Text
                                </Typography>
                                {!viewMode && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesome />}
                                        onClick={handleGeneratePrompt}
                                        disabled={isGenerating || !values.prompt_text?.trim()}
                                        sx={{ 
                                            borderColor: '#9c27b0',
                                            color: '#9c27b0',
                                            '&:hover': {
                                                borderColor: '#7b1fa2',
                                                backgroundColor: 'rgba(156, 39, 176, 0.04)'
                                            }
                                        }}
                                    >
                                        {isGenerating ? 'Generating...' : '✨ Magic Generate'}
                                    </Button>
                                )}
                            </Stack>
                            <TextField
                                fullWidth
                                multiline
                                minRows={15}
                                {...getFieldProps('prompt_text')}
                                error={Boolean(touched.prompt_text && errors.prompt_text)}
                                helperText={(touched.prompt_text && errors.prompt_text) || (viewMode ? "" : "Describe what you want, then click Magic Generate to create a professional prompt")}
                                placeholder="Describe what kind of pitch you want (e.g., 'IT services pitch for SaaS companies focusing on digital transformation')..."
                                disabled={viewMode}
                                InputProps={{ readOnly: viewMode }}
                            />
                        </Box>

                    </Stack>

                    {!viewMode && (
                        <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <LoadingButton
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                            >
                                Save
                            </LoadingButton>
                        </Box>
                    )}
                </Form>
            </FormikProvider>
        </Box>
    );
};

// --- Enrichment Prompt Form Component (No Country) ---
const EnrichmentPromptForm = ({ id, onClose, onSuccess, viewMode = false }) => {
    const [initialValues, setInitialValues] = useState({
        title: '',
        prompt_text: ''
    });
    const [isEnhancing, setIsEnhancing] = useState(false);

    useEffect(() => {
        if (id) {
            apiInstance.get(`enrichment-prompts/${id}/`)
                .then((response) => {
                    const data = response?.data?.data;
                    if (data) {
                        setInitialValues({
                            title: data.title || '',
                            prompt_text: data.prompt_text
                        });
                    }
                })
                .catch((error) => {
                    showErrorToast(error);
                    onClose();
                });
        }
    }, [id, onClose]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        prompt_text: Yup.string().required("Search criteria is required")
    });

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            const payload = {
                title: values.title,
                prompt_text: values.prompt_text
            };

            const method = id ? 'put' : 'post';
            const url = id ? `enrichment-prompts/${id}/` : 'enrichment-prompts/';

            apiInstance[method](url, payload)
                .then((response) => {
                    showToast(response?.data?.message || 'Prompt saved successfully');
                    onSuccess();
                    onClose();
                })
                .catch((error) => {
                    showErrorToast(error);
                    setSubmitting(false);
                });
        }
    });

    const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

    const handleEnhanceCriteria = async () => {
        if (!values.prompt_text?.trim()) {
            showErrorToast({ message: 'Please enter search criteria first' });
            return;
        }
        
        setIsEnhancing(true);
        try {
            const response = await apiInstance.post('enrichment-prompts/enhance-criteria/', {
                criteria: values.prompt_text
            });
            
            if (response?.data?.success) {
                setFieldValue('prompt_text', response.data.data.enhanced);
                showToast('Search criteria enhanced successfully');
            }
        } catch (error) {
            showErrorToast(error);
        } finally {
            setIsEnhancing(false);
        }
    };

    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h6">{viewMode ? "View Prompt" : (id ? "Edit Prompt" : "New Prompt")}</Typography>
                <IconButton onClick={onClose}>
                    <Icon icon={closeFill} width={24} height={24} />
                </IconButton>
            </Stack>

            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={3} sx={{ flexGrow: 1, overflowY: 'auto', pb: 2 }}>
                        <TextField
                            fullWidth
                            label="Title"
                            {...getFieldProps('title')}
                            error={Boolean(touched.title && errors.title)}
                            helperText={touched.title && errors.title}
                            placeholder="E.g., SAAS Seed Stage Companies"
                            disabled={viewMode}
                            InputProps={{ readOnly: viewMode }}
                        />

                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Search Criteria</Typography>
                                {!viewMode && (
                                    <LoadingButton
                                        size="small"
                                        variant="outlined"
                                        loading={isEnhancing}
                                        onClick={handleEnhanceCriteria}
                                        disabled={isEnhancing || !values.prompt_text?.trim()}
                                        startIcon={<AutoAwesome />}
                                        sx={{ 
                                            borderColor: '#9c27b0', 
                                            color: '#9c27b0',
                                            '&:hover': { 
                                                borderColor: '#7b1fa2', 
                                                backgroundColor: 'rgba(156, 39, 176, 0.04)' 
                                            }
                                        }}
                                    >
                                        Enhance with AI
                                    </LoadingButton>
                                )}
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                {...getFieldProps('prompt_text')}
                                error={Boolean(touched.prompt_text && errors.prompt_text)}
                                helperText={(touched.prompt_text && errors.prompt_text) || (viewMode ? "" : "Describe the type of companies you want to find. E.g., 'SAAS companies in tech with 20-50 employees who recently did a seed round'")}
                                placeholder="E.g., SAAS companies in tech with 20-50 employees who recently did a seed round"
                                disabled={viewMode}
                                InputProps={{ readOnly: viewMode }}
                            />
                        </Box>

                        <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <Typography variant="body2" color="info.darker">
                                💡 <strong>How it works:</strong> Your search criteria will be parsed by AI to find matching companies via Apollo API. 
                                Be specific about industry, employee count, funding stage, revenue, etc.
                            </Typography>
                        </Box>
                    </Stack>

                    {!viewMode && (
                        <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <LoadingButton
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                            >
                                Save
                            </LoadingButton>
                        </Box>
                    )}
                </Form>
            </FormikProvider>
        </Box>
    );
};

// --- Run Enrichment Dialog Component ---
const RunEnrichmentDialog = ({ open, onClose, onSuccess, enrichmentPrompt, promptTemplates, onCreateTemplate }) => {
    const navigate = useNavigate();
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [companyCount, setCompanyCount] = useState(5);
    const [isRunning, setIsRunning] = useState(false);
    const [errors, setErrors] = useState({ template: '', count: '' });

    const validateForm = () => {
        const newErrors = { template: '', count: '' };
        let isValid = true;

        if (!selectedTemplate) {
            newErrors.template = 'Please select a pitch template';
            isValid = false;
        }

        const count = parseInt(companyCount);
        if (!companyCount || isNaN(count)) {
            newErrors.count = 'Please enter a valid number';
            isValid = false;
        } else if (count < 1) {
            newErrors.count = 'Minimum 1 company required';
            isValid = false;
        } else if (count > 10) {
            newErrors.count = 'Maximum 10 companies allowed per run';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleRun = () => {
        if (!validateForm()) {
            return;
        }

        setIsRunning(true);
        apiInstance.post(`enrichment-prompts/${enrichmentPrompt.id}/run/`, {
            prompt_template_id: selectedTemplate.id,
            requested_count: parseInt(companyCount)
        })
            .then((response) => {
                showToast('Enrichment run started successfully');
                const runHistoryId = response?.data?.data?.run_history_id;
                onClose();
                onSuccess();
                if (runHistoryId) {
                    navigate(`/enrichment-progress/${runHistoryId}`);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsRunning(false);
            });
    };

    const handleCreateNewTemplate = () => {
        onClose();
        onCreateTemplate();
    };

    // Clear errors when values change
    const handleTemplateChange = (event, newValue) => {
        setSelectedTemplate(newValue);
        if (newValue) setErrors(prev => ({ ...prev, template: '' }));
    };

    const handleCountChange = (e) => {
        const val = e.target.value;
        setCompanyCount(val);
        if (val) setErrors(prev => ({ ...prev, count: '' }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Run Enrichment: {enrichmentPrompt?.title}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        This will search for companies matching your criteria and generate pitches using the selected template.
                    </Typography>
                    
                    <Box>
                        <Autocomplete
                            fullWidth
                            options={promptTemplates}
                            getOptionLabel={(option) => `${option.title} (${option.country?.label || 'N/A'})`}
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                            noOptionsText={
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        No templates available
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="contained" 
                                        onClick={handleCreateNewTemplate}
                                        startIcon={<Icon icon={plusFill} />}
                                        sx={{ mt: 1, backgroundColor: '#00AB55', '&:hover': { backgroundColor: '#009648' } }}
                                    >
                                        Create Template
                                    </Button>
                                </Box>
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Pitch Template *"
                                    placeholder="Choose a template"
                                    error={!!errors.template}
                                    helperText={errors.template}
                                />
                            )}
                        />
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleCreateNewTemplate}
                            startIcon={<Icon icon={plusFill} width={16} height={16} />}
                            sx={{ 
                                mt: 0.5,
                                color: '#00AB55',
                                textTransform: 'none',
                                fontSize: '12px',
                                '&:hover': { backgroundColor: 'rgba(0, 171, 85, 0.08)' }
                            }}
                        >
                            Create new template
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        type="number"
                        label="Number of Companies"
                        value={companyCount}
                        onChange={handleCountChange}
                        error={!!errors.count}
                        helperText={errors.count || "Enter 1-10 companies per run"}
                        inputProps={{ min: 1 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRunning}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleRun}
                    variant="contained"
                    loading={isRunning}
                    startIcon={<PlayArrow />}
                >
                    Run
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

// --- Outreach Drawer Component (70% width, with previews) ---
const OutreachDrawer = ({ onClose, onSuccess, countryData, allEnrichmentPrompts, allPitchTemplates, onOpenProgress }) => {
    const [title, setTitle] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [selectedPitch, setSelectedPitch] = useState(null);
    const [companyCount, setCompanyCount] = useState(5);
    const [isRunning, setIsRunning] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Runtime editable copies of prompt/pitch text (not saved)
    const [editablePromptText, setEditablePromptText] = useState('');
    const [editablePitchText, setEditablePitchText] = useState('');
    
    // Save to library states
    const [isSavingPrompt, setIsSavingPrompt] = useState(false);
    const [isSavingPitch, setIsSavingPitch] = useState(false);
    
    // Create new prompt/pitch states
    const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
    const [isCreatingPitch, setIsCreatingPitch] = useState(false);
    const [isSavingNewPrompt, setIsSavingNewPrompt] = useState(false);
    const [isSavingNewPitch, setIsSavingNewPitch] = useState(false);

    // Update editable text when selection changes
    useEffect(() => {
        setEditablePromptText(selectedPrompt?.prompt_text || '');
    }, [selectedPrompt]);

    useEffect(() => {
        setEditablePitchText(selectedPitch?.prompt_text || '');
    }, [selectedPitch]);
    
    // Check if text has been modified
    const isPromptModified = selectedPrompt && editablePromptText !== (selectedPrompt?.prompt_text || '');
    const isPitchModified = selectedPitch && editablePitchText !== (selectedPitch?.prompt_text || '');
    
    // Save prompt to library (updates existing prompt)
    const handleSavePromptToLibrary = () => {
        if (!selectedPrompt || !editablePromptText.trim()) return;
        
        setIsSavingPrompt(true);
        apiInstance.put(`enrichment-prompts/${selectedPrompt.id}/`, {
            prompt_text: editablePromptText.trim(),
        })
            .then(() => {
                showToast('Prompt saved to library successfully!');
                // Update the selected prompt's text so isPromptModified becomes false
                setSelectedPrompt(prev => ({ ...prev, prompt_text: editablePromptText.trim() }));
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsSavingPrompt(false);
            });
    };
    
    // Save pitch to library (updates existing pitch template)
    const handleSavePitchToLibrary = () => {
        if (!selectedPitch || !editablePitchText.trim()) return;
        
        setIsSavingPitch(true);
        apiInstance.put(`Prompt-templates/${selectedPitch.id}/`, {
            prompt_text: editablePitchText.trim(),
        })
            .then(() => {
                showToast('Pitch template saved to library successfully!');
                // Update the selected pitch's text so isPitchModified becomes false
                setSelectedPitch(prev => ({ ...prev, prompt_text: editablePitchText.trim() }));
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsSavingPitch(false);
            });
    };
    
    // Create new prompt (using editablePromptText from the right-side preview)
    const handleCreateNewPrompt = () => {
        if (!title.trim()) {
            setErrors(prev => ({ ...prev, title: 'Please enter a campaign title first' }));
            return;
        }
        if (!editablePromptText.trim()) {
            showErrorToast({ message: 'Please enter prompt text' });
            return;
        }
        
        setIsSavingNewPrompt(true);
        apiInstance.post('enrichment-prompts/', {
            title: title.trim(),
            prompt_text: editablePromptText.trim(),
        })
            .then((response) => {
                showToast('Prompt created successfully!');
                const newPrompt = response?.data?.data || response?.data;
                setSelectedPrompt(newPrompt);
                setIsCreatingPrompt(false);
                if (errors.prompt) setErrors(prev => ({ ...prev, prompt: '' }));
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsSavingNewPrompt(false);
            });
    };
    
    // Create new pitch template (using editablePitchText from the right-side preview)
    const handleCreateNewPitch = () => {
        if (!title.trim()) {
            setErrors(prev => ({ ...prev, title: 'Please enter a campaign title first' }));
            return;
        }
        if (!editablePitchText.trim()) {
            showErrorToast({ message: 'Please enter pitch template text' });
            return;
        }
        
        setIsSavingNewPitch(true);
        apiInstance.post('Prompt-templates/', {
            title: title.trim(),
            prompt_text: editablePitchText.trim(),
        })
            .then((response) => {
                showToast('Pitch template created successfully!');
                const newPitch = response?.data?.data || response?.data;
                setSelectedPitch(newPitch);
                setIsCreatingPitch(false);
                if (errors.pitch) setErrors(prev => ({ ...prev, pitch: '' }));
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsSavingNewPitch(false);
            });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!title.trim()) newErrors.title = 'Title is required';
        if (!selectedCountry) newErrors.country = 'Country is required';
        if (!selectedPrompt) newErrors.prompt = 'Please select a prompt';
        if (!selectedPitch) newErrors.pitch = 'Please select a pitch template';
        
        const count = parseInt(companyCount);
        if (!companyCount || isNaN(count) || count < 1) {
            newErrors.count = 'Enter a valid number (min 1)';
        } else if (count > 10) {
            newErrors.count = 'Maximum 10 companies per run';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRun = () => {
        if (!validateForm()) return;

        setIsRunning(true);
        const payload = {
            title: title.trim(),
            prompt_template_id: selectedPitch.id,
            requested_count: parseInt(companyCount),
            country_id: selectedCountry?.id,
        };
        console.log('Running campaign with payload:', payload, 'Selected country:', selectedCountry);
        // Use existing backend endpoint: enrichment-prompts/{id}/run/
        apiInstance.post(`enrichment-prompts/${selectedPrompt.id}/run/`, payload)
            .then((response) => {
                showToast('Outreach campaign started successfully!');
                const runHistoryId = response?.data?.data?.run_history_id;
                onClose();
                onSuccess();
                if (runHistoryId && onOpenProgress) {
                    onOpenProgress(runHistoryId);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsRunning(false);
            });
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" fontWeight={700}>Run Campaign</Typography>
                    <IconButton onClick={onClose}>
                        <Icon icon={closeFill} width={24} height={24} />
                    </IconButton>
                </Stack>
            </Box>

            {/* Content - Split Layout */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                {/* Left Side - Form */}
                <Box sx={{ width: '40%', p: 3, borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Campaign Title *"
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                if (errors.title) setErrors(prev => ({ ...prev, title: '' }));
                            }}
                            error={!!errors.title}
                            helperText={errors.title}
                            placeholder="E.g., Q1 2024 SAAS Outreach"
                        />

                        <Autocomplete
                            fullWidth
                            options={countryData}
                            getOptionLabel={(option) => option.label || option.name}
                            value={selectedCountry}
                            onChange={(e, newValue) => {
                                setSelectedCountry(newValue);
                                if (errors.country) setErrors(prev => ({ ...prev, country: '' }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Country *"
                                    error={!!errors.country}
                                    helperText={errors.country}
                                />
                            )}
                        />

                        <Box>
                            <Autocomplete
                                fullWidth
                                options={allEnrichmentPrompts || []}
                                getOptionLabel={(option) => option.title || ''}
                                value={selectedPrompt}
                                onChange={(e, newValue) => {
                                    setSelectedPrompt(newValue);
                                    setIsCreatingPrompt(false);
                                    if (errors.prompt) setErrors(prev => ({ ...prev, prompt: '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={isCreatingPrompt ? "Select existing or create new →" : "Select Prompt (Search Criteria) *"}
                                        error={!!errors.prompt}
                                        helperText={errors.prompt || "Search criteria to find companies"}
                                    />
                                )}
                            />
                            <Button
                                size="small"
                                onClick={() => {
                                    setIsCreatingPrompt(true);
                                    setSelectedPrompt(null);
                                    setEditablePromptText('');
                                }}
                                sx={{ mt: 0.5, textTransform: 'none' }}
                                color="primary"
                                variant="text"
                            >
                                {isCreatingPrompt ? '+ Creating New Prompt' : '+ Create New Prompt'}
                            </Button>
                        </Box>

                        <Box>
                            <Autocomplete
                                fullWidth
                                options={allPitchTemplates || []}
                                getOptionLabel={(option) => option.title || ''}
                                value={selectedPitch}
                                onChange={(e, newValue) => {
                                    setSelectedPitch(newValue);
                                    setIsCreatingPitch(false);
                                    if (errors.pitch) setErrors(prev => ({ ...prev, pitch: '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={isCreatingPitch ? "Select existing or create new →" : "Select Pitch Template *"}
                                        error={!!errors.pitch}
                                        helperText={errors.pitch || "Template for generating pitches"}
                                    />
                                )}
                            />
                            <Button
                                size="small"
                                onClick={() => {
                                    setIsCreatingPitch(true);
                                    setSelectedPitch(null);
                                    setEditablePitchText('');
                                }}
                                sx={{ mt: 0.5, textTransform: 'none' }}
                                color="primary"
                                variant="text"
                            >
                                {isCreatingPitch ? '+ Creating New Pitch' : '+ Create New Pitch Template'}
                            </Button>
                        </Box>

                        <TextField
                            fullWidth
                            type="number"
                            label="Number of Companies *"
                            value={companyCount}
                            onChange={(e) => {
                                setCompanyCount(e.target.value);
                                if (errors.count) setErrors(prev => ({ ...prev, count: '' }));
                            }}
                            error={!!errors.count}
                            helperText={errors.count || "1-10 companies per run"}
                            inputProps={{ min: 1, max: 10 }}
                        />
                    </Stack>
                </Box>

                {/* Right Side - Previews */}
                <Box sx={{ width: '60%', p: 3, overflowY: 'auto', bgcolor: '#f8f9fa' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Preview & Edit (changes apply to this run only)
                    </Typography>
                    
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {/* Prompt Preview */}
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 0 }}>
                                        📋 {isCreatingPrompt ? 'New Prompt' : 'Prompt (Search Criteria)'}
                                    </Typography>
                                    {isCreatingPrompt ? (
                                        <LoadingButton
                                            size="small"
                                            variant="contained"
                                            loading={isSavingNewPrompt}
                                            onClick={handleCreateNewPrompt}
                                            startIcon={<Icon icon={saveFill} width={16} height={16} />}
                                            sx={{ textTransform: 'none' }}
                                            disabled={!editablePromptText.trim()}
                                        >
                                            Save as New
                                        </LoadingButton>
                                    ) : isPromptModified && (
                                        <LoadingButton
                                            size="small"
                                            variant="text"
                                            loading={isSavingPrompt}
                                            onClick={handleSavePromptToLibrary}
                                            startIcon={<Icon icon={saveFill} width={16} height={16} />}
                                            sx={{ color: '#00AB55', textTransform: 'none' }}
                                        >
                                            Save to Library
                                        </LoadingButton>
                                    )}
                                </Stack>
                                {(selectedPrompt || isCreatingPrompt) ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                        value={editablePromptText}
                                        onChange={(e) => setEditablePromptText(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1 }}
                                        placeholder={isCreatingPrompt ? "Enter search criteria for finding companies..." : ""}
                                        helperText={isCreatingPrompt ? "Enter prompt text and click 'Save as New'" : (isPromptModified ? "Modified - click 'Save to Library' to update the original" : "Edit the prompt text above")}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        Select a prompt to preview
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Pitch Preview */}
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ mb: 0 }}>
                                        ✨ {isCreatingPitch ? 'New Pitch Template' : 'Pitch Template'}
                                    </Typography>
                                    {isCreatingPitch ? (
                                        <LoadingButton
                                            size="small"
                                            variant="contained"
                                            loading={isSavingNewPitch}
                                            onClick={handleCreateNewPitch}
                                            startIcon={<Icon icon={saveFill} width={16} height={16} />}
                                            sx={{ textTransform: 'none' }}
                                            disabled={!editablePitchText.trim()}
                                        >
                                            Save as New
                                        </LoadingButton>
                                    ) : isPitchModified && (
                                        <LoadingButton
                                            size="small"
                                            variant="text"
                                            loading={isSavingPitch}
                                            onClick={handleSavePitchToLibrary}
                                            startIcon={<Icon icon={saveFill} width={16} height={16} />}
                                            sx={{ color: '#00AB55', textTransform: 'none' }}
                                        >
                                            Save to Library
                                        </LoadingButton>
                                    )}
                                </Stack>
                                {(selectedPitch || isCreatingPitch) ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        maxRows={12}
                                        value={editablePitchText}
                                        onChange={(e) => setEditablePitchText(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1 }}
                                        placeholder={isCreatingPitch ? "Enter pitch template text..." : ""}
                                        helperText={isCreatingPitch ? "Enter pitch template text and click 'Save as New'" : (isPitchModified ? "Modified - click 'Save to Library' to update the original" : "Edit the pitch template above")}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        Select a pitch template to preview
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button onClick={onClose} disabled={isRunning} variant="outlined">
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleRun}
                        variant="contained"
                        loading={isRunning}
                        startIcon={<PlayArrow />}
                        sx={{ 
                            backgroundColor: '#00AB55',
                            '&:hover': { backgroundColor: '#009648' }
                        }}
                    >
                        Run Campaign
                    </LoadingButton>
                </Stack>
            </Box>
        </Box>
    );
};

// --- Campaign Detail Drawer Component ---
const CampaignDetailDrawer = ({ open, onClose, campaign, onNavigate, onExpandChange }) => {
    const [companyDetails, setCompanyDetails] = useState({});
    const [loadingCompanies, setLoadingCompanies] = useState({});
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    
    // Mark Outreach state - now as side panel instead of dialog
    const [markOutreachPanelOpen, setMarkOutreachPanelOpen] = useState(false);
    
    // Invalid company state
    const [markInvalidPanelOpen, setMarkInvalidPanelOpen] = useState(false);
    const [invalidCompanyId, setInvalidCompanyId] = useState(null);
    const [invalidReasons, setInvalidReasons] = useState([]);
    const [selectedInvalidReason, setSelectedInvalidReason] = useState('');
    const [invalidDescription, setInvalidDescription] = useState('');
    const [isMarkingInvalid, setIsMarkingInvalid] = useState(false);
    const [loadingInvalidReasons, setLoadingInvalidReasons] = useState(false);
    
    // Notify parent when expansion state changes (either company detail or mark panel or invalid panel)
    useEffect(() => {
        if (onExpandChange) {
            onExpandChange(!!selectedCompanyId || markOutreachPanelOpen || markInvalidPanelOpen);
        }
    }, [selectedCompanyId, markOutreachPanelOpen, markInvalidPanelOpen, onExpandChange]);
    const [outreachCompanyId, setOutreachCompanyId] = useState(null);
    const [outreachMethod, setOutreachMethod] = useState('email');
    const [outreachComment, setOutreachComment] = useState('');
    const [outreachDate, setOutreachDate] = useState('');
    const [outreachTime, setOutreachTime] = useState('');
    const [isMarkingOutreach, setIsMarkingOutreach] = useState(false);
    const [existingOutreachDetails, setExistingOutreachDetails] = useState(null);
    const [loadingOutreachDetails, setLoadingOutreachDetails] = useState(false);
    
    // Get all unique company IDs from all runs
    const allCompanyIds = React.useMemo(() => {
        if (!campaign) return [];
        const ids = new Set();
        campaign.runs.forEach(run => {
            (run.companies_created || []).forEach(id => ids.add(id));
        });
        return Array.from(ids);
    }, [campaign]);
    
    // Fetch company details
    const fetchCompanyDetails = async (companyId) => {
        if (companyDetails[companyId] || loadingCompanies[companyId]) return;
        
        setLoadingCompanies(prev => ({ ...prev, [companyId]: true }));
        try {
            const response = await apiInstance.get(`company/${companyId}/`);
            const data = response?.data?.data;
            if (data) {
                setCompanyDetails(prev => ({ ...prev, [companyId]: data }));
            }
        } catch (error) {
            console.error('Failed to fetch company details:', error);
            setCompanyDetails(prev => ({ ...prev, [companyId]: { error: true } }));
        } finally {
            setLoadingCompanies(prev => ({ ...prev, [companyId]: false }));
        }
    };
    
    // Fetch all company details when drawer opens
    useEffect(() => {
        if (open && allCompanyIds.length > 0) {
            allCompanyIds.forEach(id => fetchCompanyDetails(id));
        }
    }, [open, allCompanyIds]);
    
    // Reset selected company when drawer closes
    useEffect(() => {
        if (!open) {
            setSelectedCompanyId(null);
        }
    }, [open]);
    
    const handleOpenCompanyDrawer = (companyId, e) => {
        if (e) e.stopPropagation();
        // Close mark panel if open
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        setSelectedCompanyId(companyId);
    };
    
    const handleCloseCompanyDrawer = () => {
        setSelectedCompanyId(null);
    };
    
    // Mark Outreach handlers - opens as side panel
    const handleOpenMarkOutreach = async (companyId, e) => {
        if (e) e.stopPropagation();
        // Close company detail if open
        setSelectedCompanyId(null);
        setMarkInvalidPanelOpen(false);
        setInvalidCompanyId(null);
        setOutreachCompanyId(companyId);
        setExistingOutreachDetails(null);
        setMarkOutreachPanelOpen(true);
        
        // Fetch existing outreach details
        setLoadingOutreachDetails(true);
        try {
            const response = await apiInstance.get(`company/${companyId}/outreach-details/`);
            if (response.data.success && response.data.data) {
                setExistingOutreachDetails(response.data.data);
            } else {
                // No existing outreach - show form with defaults
                const now = new Date();
                setOutreachMethod('email');
                setOutreachComment('');
                setOutreachDate(now.toISOString().split('T')[0]);
                setOutreachTime(now.toTimeString().slice(0, 5));
            }
        } catch (error) {
            // 404 means not outreached yet - show form
            const now = new Date();
            setOutreachMethod('email');
            setOutreachComment('');
            setOutreachDate(now.toISOString().split('T')[0]);
            setOutreachTime(now.toTimeString().slice(0, 5));
        } finally {
            setLoadingOutreachDetails(false);
        }
    };
    
    const handleCloseMarkOutreach = () => {
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        setExistingOutreachDetails(null);
    };
    
    const handleSubmitMarkOutreach = async () => {
        if (!outreachComment.trim()) {
            showErrorToast({ message: 'Comment is required' });
            return;
        }
        
        setIsMarkingOutreach(true);
        try {
            const outreachedAt = new Date(`${outreachDate}T${outreachTime}:00`).toISOString();
            const response = await apiInstance.post(
                `company/${outreachCompanyId}/mark-outreached/`,
                {
                    comment: outreachComment.trim(),
                    outreach_method: outreachMethod,
                    outreached_at: outreachedAt
                }
            );
            
            if (response.data.success) {
                showToast('Company marked as outreached');
                handleCloseMarkOutreach();
                // Refresh company details - refetch without clearing existing data first
                try {
                    const refreshResponse = await apiInstance.get(`company/${outreachCompanyId}/`);
                    const refreshedData = refreshResponse?.data?.data;
                    if (refreshedData) {
                        setCompanyDetails(prev => ({ ...prev, [outreachCompanyId]: refreshedData }));
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh company:', refreshError);
                }
            }
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Failed to mark as outreached');
        } finally {
            setIsMarkingOutreach(false);
        }
    };
    
    // Invalid Company handlers
    const fetchInvalidReasons = async () => {
        setLoadingInvalidReasons(true);
        try {
            const response = await apiInstance.get('company/invalid-reasons/');
            if (response.data.success) {
                setInvalidReasons(response.data.data || []);
            } else if (response.data && Array.isArray(response.data)) {
                setInvalidReasons(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch invalid reasons:', error);
            showErrorToast('Failed to load invalid reasons');
        } finally {
            setLoadingInvalidReasons(false);
        }
    };
    
    const handleOpenMarkInvalid = async (companyId, e) => {
        if (e) e.stopPropagation();
        // Close other panels
        setSelectedCompanyId(null);
        setMarkOutreachPanelOpen(false);
        setOutreachCompanyId(null);
        
        setInvalidCompanyId(companyId);
        setSelectedInvalidReason('');
        setInvalidDescription('');
        await fetchInvalidReasons();
        setMarkInvalidPanelOpen(true);
    };
    
    const handleCloseMarkInvalid = () => {
        setMarkInvalidPanelOpen(false);
        setInvalidCompanyId(null);
        setSelectedInvalidReason('');
        setInvalidDescription('');
    };
    
    const handleSubmitMarkInvalid = async () => {
        if (!selectedInvalidReason) {
            showErrorToast('Please select a reason');
            return;
        }
        
        setIsMarkingInvalid(true);
        try {
            const response = await apiInstance.post(
                `company/${invalidCompanyId}/mark-invalid/`,
                {
                    reason_id: selectedInvalidReason,
                    description: invalidDescription.trim() || null
                }
            );
            
            if (response.data.success) {
                showToast('Company marked as invalid');
                handleCloseMarkInvalid();
                // Refresh company details
                try {
                    const refreshResponse = await apiInstance.get(`company/${invalidCompanyId}/`);
                    const refreshedData = refreshResponse?.data?.data;
                    if (refreshedData) {
                        setCompanyDetails(prev => ({ ...prev, [invalidCompanyId]: refreshedData }));
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh company:', refreshError);
                }
            }
        } catch (error) {
            console.error('Mark invalid error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to mark as invalid');
        } finally {
            setIsMarkingInvalid(false);
        }
    };
    
    // Check if company is at 0% stage (stage_id = 1 is Outreach 0%)
    // Also show for companies where stage isn't set (null/undefined) since they're new
    const isZeroPercentStage = (company) => {
        // current_stage is just the stage ID number (1 = 0% Outreach)
        // Show Mark button if stage is 1 OR if stage isn't set yet (new companies)
        const stage = company?.current_stage;
        return stage === 1 || stage === null || stage === undefined;
    };
    
    if (!campaign) return null;
    
    const totalCreated = campaign.runs.reduce((sum, run) => sum + (run.actual_count || 0), 0);
    const totalPitches = campaign.runs.reduce((sum, run) => sum + (run.pitches_generated || 0), 0);
    const totalAt10Percent = campaign.runs.reduce((sum, run) => sum + (run.companies_at_10_percent || 0), 0);
    const successRate = totalCreated > 0 ? Math.round((totalAt10Percent / totalCreated) * 100) : 0;
    
    const isExpanded = !!selectedCompanyId || markOutreachPanelOpen || markInvalidPanelOpen;
    
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row' }}>
            {/* LEFT PANEL - Companies List */}
            <Box sx={{ 
                width: isExpanded ? '40%' : '100%',
                minWidth: isExpanded ? 320 : 'auto',
                borderRight: isExpanded ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>{campaign.title}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {campaign.country} • {campaign.runs.length} run{campaign.runs.length > 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <IconButton onClick={onClose} size="small">
                                <Icon icon={closeFill} width={20} height={20} />
                            </IconButton>
                        </Stack>
                        
                        {/* Summary Stats - Compact */}
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Companies</Typography>
                                <Typography variant="subtitle2">{totalCreated}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Pitches</Typography>
                                <Typography variant="subtitle2">{totalPitches}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Success</Typography>
                                <Typography variant="subtitle2">{successRate}%</Typography>
                            </Box>
                        </Stack>
                    </Box>
                    
                    {/* Companies List */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                        {allCompanyIds.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                                No companies created yet
                            </Typography>
                        ) : (
                            <Stack spacing={0.75}>
                                {allCompanyIds.map((companyId) => {
                                    const company = companyDetails[companyId];
                                    const isLoading = loadingCompanies[companyId];
                                    const isSelected = selectedCompanyId === companyId;
                                    
                                    return (
                                        <Paper
                                            key={companyId}
                                            onClick={() => handleOpenCompanyDrawer(companyId)}
                                            sx={{
                                                p: 1,
                                                cursor: 'pointer',
                                                border: isSelected ? '2px solid #00AB55' : '1px solid #e0e0e0',
                                                bgcolor: isSelected ? 'rgba(0, 171, 85, 0.08)' : 'transparent',
                                                '&:hover': { bgcolor: 'rgba(0, 171, 85, 0.04)', borderColor: '#00AB55' }
                                            }}
                                        >
                                            {isLoading ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CircularProgress size={14} />
                                                    <Typography variant="caption">Loading #{companyId}...</Typography>
                                                </Stack>
                                            ) : company?.error ? (
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Chip label={`#${companyId}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    <Typography variant="caption" color="text.secondary">Error</Typography>
                                                </Stack>
                                            ) : company ? (
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                    {/* Left: Company Info */}
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                                        <Chip 
                                                            label={`#${companyId}`} 
                                                            size="small" 
                                                            color={isSelected ? "success" : "primary"}
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.65rem', minWidth: 45 }} 
                                                        />
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography 
                                                                variant="body2" 
                                                                fontWeight={isSelected ? 600 : 500}
                                                                sx={{ 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {company.company_name || 'Unknown'}
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                                                {company.country?.label && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        📍 {company.country.label}
                                                                    </Typography>
                                                                )}
                                                                {company.apollo_enrichment_data?.organization?.industry && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        • 🏢 {company.apollo_enrichment_data.organization.industry}
                                                                    </Typography>
                                                                )}
                                                                {company.created && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        • {new Date(company.created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(company.created).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                    
                                                    {/* Right: Actions - Show Mark/Done for all companies in campaign drawer */}
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        {company.is_outreached ? (
                                                            <Tooltip title="View Outreach Details">
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="success"
                                                                    onClick={(e) => handleOpenMarkOutreach(companyId, e)}
                                                                    sx={{ 
                                                                        minWidth: 'auto', 
                                                                        px: 0.75, 
                                                                        py: 0.25,
                                                                        fontSize: '0.6rem',
                                                                        height: 22
                                                                    }}
                                                                >
                                                                    ✅ Outreached
                                                                </Button>
                                                            </Tooltip>
                                                        ) : (
                                                            <>
                                                                <Tooltip title="Mark as Outreached">
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        onClick={(e) => handleOpenMarkOutreach(companyId, e)}
                                                                        sx={{ 
                                                                            minWidth: 'auto', 
                                                                            px: 0.75, 
                                                                            py: 0.25,
                                                                            fontSize: '0.6rem',
                                                                            height: 22,
                                                                            bgcolor: '#1976d2',
                                                                            color: 'white',
                                                                            '&:hover': { bgcolor: '#1565c0' }
                                                                        }}
                                                                    >
                                                                        📤 Mark
                                                                    </Button>
                                                                </Tooltip>
                                                                <Tooltip title="Mark as Invalid">
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="error"
                                                                        onClick={(e) => handleOpenMarkInvalid(companyId, e)}
                                                                        sx={{ 
                                                                            minWidth: 'auto', 
                                                                            px: 0.75, 
                                                                            py: 0.25,
                                                                            fontSize: '0.6rem',
                                                                            height: 22
                                                                        }}
                                                                    >
                                                                        ⚠️ Invalid
                                                                    </Button>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        <Tooltip title="View Details & Pitch">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={(e) => handleOpenCompanyDrawer(companyId, e)}
                                                                sx={{ 
                                                                    p: 0.5,
                                                                    bgcolor: isSelected ? '#00AB55' : 'transparent',
                                                                    color: isSelected ? 'white' : 'primary.main',
                                                                    '&:hover': { bgcolor: isSelected ? '#00AB55' : 'rgba(0,0,0,0.04)' }
                                                                }}
                                                            >
                                                                <Visibility sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            ) : (
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Chip label={`#${companyId}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    <IconButton size="small" onClick={(e) => handleOpenCompanyDrawer(companyId, e)} sx={{ p: 0.5 }}>
                                                        <Visibility sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Stack>
                                            )}
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                </Box>
                
            {/* RIGHT PANEL - Company Detail (only shows when company selected) */}
            {selectedCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa' }}>
                    <AddCompany 
                        id={selectedCompanyId} 
                        setOpenDrawer={handleCloseCompanyDrawer}
                        companyData={companyDetails[selectedCompanyId]}
                        defaultTab="pitch"
                    />
                </Box>
            )}
            
            {/* RIGHT PANEL - Mark Outreach Form or Details (only shows when mark panel is open) */}
            {markOutreachPanelOpen && outreachCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '1.5rem' }}>📤</span>
                            <Typography variant="h6">
                                {existingOutreachDetails ? 'Outreach Details' : 'Mark as Outreached'}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseMarkOutreach} size="small">
                            <Icon icon={closeFill} width={20} height={20} />
                        </IconButton>
                    </Box>
                    
                    {/* Company Info */}
                    {companyDetails[outreachCompanyId] && (
                        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(0, 171, 85, 0.08)', border: '1px solid #00AB55' }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {companyDetails[outreachCompanyId].company_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                #{outreachCompanyId} • {companyDetails[outreachCompanyId].country?.label}
                            </Typography>
                        </Paper>
                    )}
                    
                    {/* Loading state */}
                    {loadingOutreachDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : existingOutreachDetails ? (
                        /* Show existing outreach details */
                        <Stack spacing={2}>
                            <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                                    ✅ Already Outreached
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Method</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.outreach_method_display || existingOutreachDetails.outreach_method}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Outreached At</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.outreached_at ? 
                                                new Date(existingOutreachDetails.outreached_at).toLocaleString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Marked By</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {existingOutreachDetails.marked_by_name || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Comment</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {existingOutreachDetails.comment || 'No comment'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                            <Button onClick={handleCloseMarkOutreach} variant="outlined">
                                Close
                            </Button>
                        </Stack>
                    ) : (
                        /* Show form to mark as outreached */
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Outreach Method *
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={outreachMethod}
                                        onChange={(e) => setOutreachMethod(e.target.value)}
                                    >
                                        <MenuItem value="email">📧 Email</MenuItem>
                                        <MenuItem value="call">📞 Call</MenuItem>
                                        <MenuItem value="linkedin">💼 LinkedIn</MenuItem>
                                        <MenuItem value="other">📝 Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Stack direction="row" spacing={1}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Date *
                                    </Typography>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        value={outreachDate}
                                        onChange={(e) => setOutreachDate(e.target.value)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Time *
                                    </Typography>
                                    <TextField
                                        type="time"
                                        fullWidth
                                        size="small"
                                        value={outreachTime}
                                        onChange={(e) => setOutreachTime(e.target.value)}
                                    />
                                </Box>
                            </Stack>
                            
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Comment *
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    placeholder="Enter details about the outreach..."
                                    value={outreachComment}
                                    onChange={(e) => setOutreachComment(e.target.value)}
                                />
                            </Box>
                            
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button onClick={handleCloseMarkOutreach} disabled={isMarkingOutreach}>
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="success"
                                    onClick={handleSubmitMarkOutreach}
                                    disabled={isMarkingOutreach || !outreachComment.trim()}
                                >
                                    {isMarkingOutreach ? 'Saving...' : 'Mark as Outreached'}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            )}
            {/* RIGHT PANEL - Mark Invalid Form (side-by-side like Mark Outreach) */}
            {markInvalidPanelOpen && invalidCompanyId && (
                <Box sx={{ width: '60%', overflow: 'auto', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
                    {/* Header - Orange like EditJourney */}
                    <Box sx={{ 
                        bgcolor: '#ff9800', 
                        color: 'white', 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between' 
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>⚠️</span>
                            <Typography variant="h6">Mark as Invalid</Typography>
                        </Box>
                        <IconButton onClick={handleCloseMarkInvalid} sx={{ color: 'white' }}>
                            <Icon icon={closeFill} width={20} height={20} />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                        {companyDetails[invalidCompanyId] && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                                <Typography variant="h6">{companyDetails[invalidCompanyId]?.company_name}</Typography>
                            </Box>
                        )}
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reason *</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedInvalidReason}
                                    onChange={(e) => setSelectedInvalidReason(e.target.value)}
                                    displayEmpty
                                    disabled={loadingInvalidReasons}
                                >
                                    <MenuItem value="" disabled>
                                        {loadingInvalidReasons ? 'Loading reasons...' : 'Select a reason'}
                                    </MenuItem>
                                    {invalidReasons.map((reason) => (
                                        <MenuItem key={reason.id} value={reason.id}>
                                            {reason.label || reason.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Additional Details (Optional)</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Enter additional details about why this company is invalid..."
                                value={invalidDescription}
                                onChange={(e) => setInvalidDescription(e.target.value)}
                            />
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                            <Typography variant="caption" color="warning.dark">
                                ⚠️ This company will be moved to the 5% stage and marked as invalid.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseMarkInvalid} disabled={isMarkingInvalid}>
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            color="warning"
                            onClick={handleSubmitMarkInvalid}
                            disabled={isMarkingInvalid || !selectedInvalidReason}
                            startIcon={isMarkingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isMarkingInvalid ? 'Saving...' : 'Mark as Invalid'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// --- Expandable Campaign Row Component ---
const CampaignRow = ({ campaign, onNavigate, onDeleteRun, onDeleteCampaign, onViewDetails, onRerun, isRerunning, onViewProgress }) => {
    const [open, setOpen] = useState(false);
    const [runDetails, setRunDetails] = useState({}); // Cache fetched details by run ID
    const [loadingRuns, setLoadingRuns] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteRunId, setDeleteRunId] = useState(null);
    
    // Calculate totals
    const totalReq = campaign.runs.reduce((sum, run) => sum + (run.requested_count || 0), 0);
    const totalCreated = campaign.runs.reduce((sum, run) => sum + (run.actual_count || 0), 0);
    const totalPitches = campaign.runs.reduce((sum, run) => sum + (run.pitches_generated || 0), 0);
    const totalAt10Percent = campaign.runs.reduce((sum, run) => sum + (run.companies_at_10_percent || 0), 0);
    const successRate = totalCreated > 0 ? Math.round((totalAt10Percent / totalCreated) * 100) : 0;
    const successColor = successRate >= 50 ? 'success' : successRate >= 25 ? 'warning' : 'default';
    
    // Get latest run info
    const allCompleted = campaign.runs.every(r => r.status === 'completed');
    const hasInProgress = campaign.runs.some(r => !['completed', 'failed', 'no_new_companies'].includes(r.status));
    
    // Fetch run details (with companies_created) when expanding
    const fetchRunDetails = async (runId) => {
        if (runDetails[runId] || loadingRuns[runId]) return;
        
        setLoadingRuns(prev => ({ ...prev, [runId]: true }));
        try {
            const response = await apiInstance.get(`enrichment-run-history/${runId}/`);
            const data = response?.data?.data;
            if (data) {
                setRunDetails(prev => ({ ...prev, [runId]: data }));
            }
        } catch (error) {
            console.error('Failed to fetch run details:', error);
        } finally {
            setLoadingRuns(prev => ({ ...prev, [runId]: false }));
        }
    };
    
    // Fetch all run details when opened
    useEffect(() => {
        if (open) {
            campaign.runs.forEach(run => fetchRunDetails(run.id));
        }
    }, [open]);
    
    return (
        <>
            <TableRow 
                sx={{ 
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor: open ? 'rgba(0, 171, 85, 0.04)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    cursor: 'pointer'
                }}
                onClick={() => setOpen(!open)}
            >
                <TableCell sx={{ width: 40, p: 1 }}>
                    <IconButton size="small">
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row" sx={{ fontWeight: 500, width: '20%' }}>
                    {campaign.title}
                    <Typography variant="caption" color="text.secondary" display="block">
                        {campaign.runs.length} run{campaign.runs.length > 1 ? 's' : ''}
                    </Typography>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>{campaign.country || '-'}</TableCell>
                <TableCell sx={{ width: '8%' }}>
                    <Chip 
                        size="small" 
                        label={hasInProgress ? 'in progress' : allCompleted ? 'completed' : 'mixed'}
                        color={hasInProgress ? 'info' : allCompleted ? 'success' : 'warning'}
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: '6%' }}>{totalReq}</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>{totalCreated}</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>{totalPitches}</TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip 
                            size="small" 
                            label={`${successRate}% (${totalAt10Percent}/${totalCreated})`}
                            color={successColor}
                            variant="outlined"
                        />
                        <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button 
                            size="small" 
                            variant="outlined"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onRerun(campaign);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={isRerunning}
                            startIcon={isRerunning ? <CircularProgress size={14} /> : <Replay sx={{ fontSize: 16 }} />}
                            sx={{ 
                                fontSize: '0.7rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 75,
                                whiteSpace: 'nowrap',
                                textTransform: 'none'
                            }}
                        >
                            Re-run
                        </Button>
                        <Tooltip title="View Details">
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onViewDetails(campaign);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{ color: 'primary.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Campaign">
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDeleteRunId(null);
                                    setDeleteConfirmOpen(true);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{ color: 'error.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        </Stack>
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1, maxHeight: 300, overflowY: 'auto' }}>
                            {campaign.runs.map((run, index) => {
                                const details = runDetails[run.id];
                                const companies = details?.companies_created || run.companies_created || [];
                                const isLoading = loadingRuns[run.id];
                                
                                return (
                                    <Box 
                                        key={run.id} 
                                        sx={{ 
                                            mb: 2, 
                                            p: 2, 
                                            bgcolor: 'grey.50', 
                                            borderRadius: 1,
                                            border: '1px solid #e0e0e0'
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="subtitle2">
                                                    Run #{campaign.runs.length - index}
                                                </Typography>
                                                <Chip 
                                                    size="small" 
                                                    label={run.status}
                                                    color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'info'}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {run.created ? new Date(run.created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    by {run.triggered_by?.first_name || '-'}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {run.actual_count || 0} companies created
                                                </Typography>
                                                <Tooltip title="View Progress Details">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onViewProgress(run.id);
                                                        }}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        sx={{ color: 'primary.main', opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Run">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setDeleteRunId(run.id);
                                                            setDeleteConfirmOpen(true);
                                                        }}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        sx={{ color: 'error.main', opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>
                    {deleteRunId ? 'Delete Run History' : 'Delete Campaign'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteRunId 
                            ? 'Are you sure you want to delete this run history? This will not delete the companies created.'
                            : `Are you sure you want to delete all ${campaign.runs.length} run(s) for "${campaign.title}"? This will not delete the companies created.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            if (deleteRunId) {
                                onDeleteRun(deleteRunId);
                            } else {
                                onDeleteCampaign(campaign.runs.map(r => r.id));
                            }
                            setDeleteConfirmOpen(false);
                        }}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// --- Consolidated Campaign Table Component ---
const ConsolidatedCampaignTable = ({ historyData, loading, onNavigate, onDeleteRun, onDeleteCampaign, onViewDetails, onRerun, rerunningCampaign, onViewProgress }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Group runs by campaign (using enrichment_prompt title or campaign title)
    const groupedCampaigns = React.useMemo(() => {
        const groups = {};
        
        historyData.forEach(run => {
            const campaignKey = run.title || run.enrichment_prompt?.title || 'Unknown Campaign';
            // Use effective_country (which considers country_override) instead of enrichment_prompt's country
            const country = run.effective_country?.label || run.enrichment_prompt?.country || '-';
            
            if (!groups[campaignKey]) {
                groups[campaignKey] = {
                    title: campaignKey,
                    country: country,
                    runs: []
                };
            }
            groups[campaignKey].runs.push(run);
        });
        
        // Sort runs within each group by date (newest first)
        Object.values(groups).forEach(group => {
            group.runs.sort((a, b) => new Date(b.created) - new Date(a.created));
        });
        
        // Convert to array and sort by latest run date
        return Object.values(groups).sort((a, b) => {
            const aDate = new Date(a.runs[0]?.created || 0);
            const bDate = new Date(b.runs[0]?.created || 0);
            return bDate - aDate;
        });
    }, [historyData]);
    
    const paginatedCampaigns = groupedCampaigns.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading && historyData.length === 0) {
        return (
            <Stack spacing={1} sx={{ py: 2 }}>
                {[...Array(5)].map((_, index) => (
                    <Skeleton key={index} variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                ))}
            </Stack>
        );
    }

    return (
        <Paper sx={{ 
            width: '100%', 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>
            {/* Sticky Header */}
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell sx={{ fontWeight: 600, width: '20%' }}>Campaign</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '10%' }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '8%' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '6%' }} align="center">Req</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '8%' }} align="center">Created</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '8%' }} align="center">Pitches</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Success %</TableCell>
                    </TableRow>
                </TableHead>
            </Table>
            
            {/* Scrollable Body */}
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableBody>
                        {paginatedCampaigns.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No campaigns found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedCampaigns.map((campaign) => (
                                <CampaignRow 
                                    key={campaign.title} 
                                    campaign={campaign} 
                                    onNavigate={onNavigate}
                                    onDeleteRun={onDeleteRun}
                                    onDeleteCampaign={onDeleteCampaign}
                                    onViewDetails={onViewDetails}
                                    onRerun={onRerun}
                                    isRerunning={rerunningCampaign === campaign.title}
                                    onViewProgress={onViewProgress}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Sticky Footer */}
            <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={groupedCampaigns.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                sx={{ borderTop: '1px solid #e0e0e0', flexShrink: 0 }}
            />
        </Paper>
    );
};

// --- Main List Component ---
export default function PromptLibrary() {
    const matches = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userInfo = UserInfo();
    const currentUserId = userInfo?.pk;

    // Initialize tabs from URL params or defaults
    const getInitialMainTab = () => {
        const tab = searchParams.get('tab');
        if (tab === 'prompt') return 1;
        if (tab === 'pitch') return 2;
        return 0; // default to 'outreach'
    };

    // Main tab state (0 = Outreach, 1 = Prompt, 2 = Pitch)
    const [mainTab, setMainTab] = useState(getInitialMainTab);
    
    // Update URL when tabs change
    const updateUrlParams = useCallback((tab) => {
        const params = new URLSearchParams();
        const tabNames = ['outreach', 'prompt', 'pitch'];
        params.set('tab', tabNames[tab] || 'outreach');
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    const [isLoading, setIsLoading] = useState(true);
    const [promptData, setPromptData] = useState([]);
    const [count, setCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState({});
    
    // Enrichment prompts state
    const [enrichmentData, setEnrichmentData] = useState([]);
    const [enrichmentCount, setEnrichmentCount] = useState(0);
    const [enrichmentPage, setEnrichmentPage] = useState(0);
    const [enrichmentLoading, setEnrichmentLoading] = useState(true);
    
    // Run history state
    const [historyData, setHistoryData] = useState([]);
    const [historyCount, setHistoryCount] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(true);
    
    // Country filter state
    const [countryData, setCountryData] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    
    // All prompt templates for dropdown
    const [allPromptTemplates, setAllPromptTemplates] = useState([]);

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPromptId, setSelectedPromptId] = useState(null);
    const [drawerType, setDrawerType] = useState('pitch'); // 'pitch' or 'enrichment'
    const [isViewMode, setIsViewMode] = useState(false); // true for view-only mode

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [promptToDelete, setPromptToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteType, setDeleteType] = useState('pitch'); // 'pitch' or 'enrichment'
    
    // Run dialog state
    const [runDialogOpen, setRunDialogOpen] = useState(false);
    const [enrichmentToRun, setEnrichmentToRun] = useState(null);
    
    // Campaign detail drawer state
    const [campaignDetailDrawerOpen, setCampaignDetailDrawerOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);
    
    const handleOpenCampaignDetail = (campaign) => {
        setSelectedCampaign(campaign);
        setCampaignDetailDrawerOpen(true);
        setIsCompanyExpanded(false);
    };
    
    const handleCloseCampaignDetail = () => {
        setCampaignDetailDrawerOpen(false);
        setSelectedCampaign(null);
        setIsCompanyExpanded(false);
    };
    
    // Progress drawer state
    const [progressDrawerOpen, setProgressDrawerOpen] = useState(false);
    const [selectedRunHistoryId, setSelectedRunHistoryId] = useState(null);
    const [isProgressExpanded, setIsProgressExpanded] = useState(false);
    
    const handleOpenProgressDrawer = (runHistoryId) => {
        setSelectedRunHistoryId(runHistoryId);
        setProgressDrawerOpen(true);
        setIsProgressExpanded(false);
    };
    
    const handleCloseProgressDrawer = () => {
        setProgressDrawerOpen(false);
        setSelectedRunHistoryId(null);
        setIsProgressExpanded(false);
    };
    
    // Re-run campaign state
    const [rerunningCampaign, setRerunningCampaign] = useState(null);
    const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
    const [pendingRerunCampaign, setPendingRerunCampaign] = useState(null);
    
    const handleRerunCampaign = (campaign) => {
        // Show confirmation dialog first
        setPendingRerunCampaign(campaign);
        setRerunConfirmOpen(true);
    };
    
    const handleConfirmRerun = () => {
        const campaign = pendingRerunCampaign;
        setRerunConfirmOpen(false);
        setPendingRerunCampaign(null);
        
        if (!campaign) return;
        
        // Get the latest run from this campaign to extract parameters
        const latestRun = campaign.runs[0]; // runs are sorted newest first
        
        if (!latestRun) {
            showErrorToast({ message: 'No run data available to re-run' });
            return;
        }
        
        // Extract parameters from the latest run
        const enrichmentPromptId = latestRun.enrichment_prompt?.id;
        const promptTemplateId = latestRun.prompt_template?.id;
        const countryId = latestRun.effective_country?.id || latestRun.country_override?.id;
        const requestedCount = latestRun.requested_count || 5;
        const title = campaign.title;
        
        if (!enrichmentPromptId) {
            showErrorToast({ message: 'Missing enrichment prompt information' });
            return;
        }
        
        if (!promptTemplateId) {
            showErrorToast({ message: 'Missing pitch template information' });
            return;
        }
        
        setRerunningCampaign(campaign.title);
        
        const payload = {
            title: title,
            prompt_template_id: promptTemplateId,
            requested_count: requestedCount,
            country_id: countryId,
        };
        
        apiInstance.post(`enrichment-prompts/${enrichmentPromptId}/run/`, payload)
            .then((response) => {
                showToast('Campaign re-run started successfully!');
                const runHistoryId = response?.data?.data?.run_history_id;
                handleRefresh();
                if (runHistoryId) {
                    // Open progress drawer instead of navigating to page
                    handleOpenProgressDrawer(runHistoryId);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setRerunningCampaign(null);
            });
    };
    
    const handleCancelRerun = () => {
        setRerunConfirmOpen(false);
        setPendingRerunCampaign(null);
    };

    const debouncedSearch = debounce((query) => {
        setSearchQuery(query);
    }, 1000);

    const getCountries = () => {
        apiInstance.get('location/?short_details=true')
            .then((response) => {
                setCountryData(response?.data?.data || []);
            })
            .catch((error) => {
                console.error('Error fetching countries:', error);
            });
    };
    
    const getAllPromptTemplates = () => {
        apiInstance.get('Prompt-templates/?page_size=100&is_active=true')
            .then((response) => {
                // Handle both response formats
                const templates = response?.data?.results?.data || response?.data?.data || [];
                setAllPromptTemplates(templates);
            })
            .catch((error) => {
                console.error('Error fetching prompt templates:', error);
            });
    };
    
    // All enrichment prompts for dropdown in OutreachDrawer
    const [allEnrichmentPrompts, setAllEnrichmentPrompts] = useState([]);
    
    const getAllEnrichmentPrompts = () => {
        apiInstance.get('enrichment-prompts/?page_size=100&is_active=true')
            .then((response) => {
                const prompts = response?.data?.results?.data || response?.data?.data || [];
                setAllEnrichmentPrompts(prompts);
            })
            .catch((error) => {
                console.error('Error fetching enrichment prompts:', error);
            });
    };

    const getPrompts = (page, pageSize, query = '', sort = {}, countryId = null, isActive = true) => {
        setIsLoading(true);
        let apiUrl = `Prompt-templates/?page=${page + 1}&page_size=${pageSize}&is_active=${isActive}`;
        
        if (query) apiUrl += `&search=${query}`;
        if (countryId) apiUrl += `&country_id=${countryId}`;
        if (sort.name && sort.direction) {
            apiUrl += `&sortfield=${sort.name}&sortdirection=${sort.direction}`;
        }

        apiInstance.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setPromptData(response.data.results.data || []);
                    setCount(response.data.count || 0);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    
    const getEnrichmentPrompts = (page, pageSize, isActive = true) => {
        setEnrichmentLoading(true);
        let apiUrl = `enrichment-prompts/?page=${page + 1}&page_size=${pageSize}&is_active=${isActive}`;

        apiInstance.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setEnrichmentData(response.data.results.data || []);
                    setEnrichmentCount(response.data.count || 0);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setEnrichmentLoading(false);
            });
    };
    
    const getRunHistory = (page, pageSize) => {
        setHistoryLoading(true);
        let apiUrl = `enrichment-run-history/?page=${page + 1}&page_size=${pageSize}`;

        apiInstance.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setHistoryData(response.data.results.data || []);
                    setHistoryCount(response.data.count || 0);
                }
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setHistoryLoading(false);
            });
    };

    const handleDeleteRun = (runId) => {
        apiInstance.delete(`enrichment-run-history/${runId}/`)
            .then(() => {
                showToast('Run history deleted successfully');
                getRunHistory(enrichmentPage, rowsPerPage);
            })
            .catch((error) => {
                showErrorToast(error);
            });
    };

    const handleDeleteCampaign = (runIds) => {
        apiInstance.post('enrichment-run-history/bulk-delete/', { ids: runIds })
            .then((response) => {
                showToast(response?.data?.message || 'Campaign deleted successfully');
                getRunHistory(enrichmentPage, rowsPerPage);
            })
            .catch((error) => {
                showErrorToast(error);
            });
    };

    useEffect(() => {
        getCountries();
        getAllPromptTemplates();
        getAllEnrichmentPrompts();
    }, []);

    useEffect(() => {
        if (mainTab === 0) {
            // Outreach tab (campaigns/history)
            getRunHistory(enrichmentPage, rowsPerPage);
        } else if (mainTab === 1) {
            // Prompt tab (search criteria)
            getEnrichmentPrompts(enrichmentPage, rowsPerPage, true);
        } else if (mainTab === 2) {
            // Pitch tab (templates)
            getPrompts(currentPage, rowsPerPage, searchQuery, sortOrder, selectedCountry?.id, true);
        }
    }, [mainTab, currentPage, enrichmentPage, rowsPerPage, searchQuery, sortOrder, selectedCountry]);

    const handleOpenDrawer = (id = null) => {
        setSelectedPromptId(id);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedPromptId(null);
    };

    const handleRefresh = () => {
        if (mainTab === 0) {
            getRunHistory(enrichmentPage, rowsPerPage);
        } else if (mainTab === 1) {
            getEnrichmentPrompts(enrichmentPage, rowsPerPage, true);
        } else if (mainTab === 2) {
            getPrompts(currentPage, rowsPerPage, searchQuery, sortOrder, selectedCountry?.id, true);
        }
        getAllPromptTemplates();
        getAllEnrichmentPrompts();
    };

    const handleMainTabChange = (newValue) => {
        setMainTab(newValue);
        setCurrentPage(0);
        setEnrichmentPage(0);
        updateUrlParams(newValue);
    };
    
    const handleOpenPitchDrawer = (id = null) => {
        setSelectedPromptId(id);
        setDrawerType('pitch');
        setIsViewMode(false);
        setDrawerOpen(true);
    };
    
    const handleOpenPitchViewDrawer = (id) => {
        setSelectedPromptId(id);
        setDrawerType('pitch');
        setIsViewMode(true);
        setDrawerOpen(true);
    };
    
    const handleOpenEnrichmentDrawer = (id = null) => {
        setSelectedPromptId(id);
        setDrawerType('enrichment');
        setIsViewMode(false);
        setDrawerOpen(true);
    };
    
    const handleOpenPromptViewDrawer = (id) => {
        setSelectedPromptId(id);
        setDrawerType('enrichment');
        setIsViewMode(true);
        setDrawerOpen(true);
    };
    
    // Outreach drawer state
    const [outreachDrawerOpen, setOutreachDrawerOpen] = useState(false);
    
    const handleOpenOutreachDrawer = () => {
        setOutreachDrawerOpen(true);
    };
    
    const handleCloseOutreachDrawer = () => {
        setOutreachDrawerOpen(false);
    };

    const handleToggleActive = (id, currentStatus) => {
        apiInstance.put(`Prompt-templates/${id}/`, { is_active: !currentStatus })
            .then((response) => {
                showToast(currentStatus ? 'Prompt deactivated successfully' : 'Prompt activated successfully');
                handleRefresh();
            })
            .catch((error) => {
                showErrorToast(error);
            });
    };
    
    const handleToggleEnrichmentActive = (id, currentStatus) => {
        apiInstance.put(`enrichment-prompts/${id}/`, { is_active: !currentStatus })
            .then((response) => {
                showToast(currentStatus ? 'Enrichment prompt deactivated successfully' : 'Enrichment prompt activated successfully');
                handleRefresh();
            })
            .catch((error) => {
                showErrorToast(error);
            });
    };

    const handleDeleteClick = (id, title, type = 'pitch') => {
        setPromptToDelete({ id, title });
        setDeleteType(type);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!promptToDelete) return;
        
        setIsDeleting(true);
        const endpoint = deleteType === 'enrichment' ? 'enrichment-prompts' : 'Prompt-templates';
        apiInstance.delete(`${endpoint}/${promptToDelete.id}/`)
            .then((response) => {
                showToast(`${deleteType === 'enrichment' ? 'Enrichment prompt' : 'Prompt'} deleted successfully`);
                setDeleteDialogOpen(false);
                setPromptToDelete(null);
                handleRefresh();
            })
            .catch((error) => {
                showErrorToast(error);
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setPromptToDelete(null);
    };
    
    const handleRunClick = (enrichmentPrompt) => {
        setEnrichmentToRun(enrichmentPrompt);
        setRunDialogOpen(true);
    };
    
    const handleRunDialogClose = () => {
        setRunDialogOpen(false);
        setEnrichmentToRun(null);
    };

    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                display: false
            }
        },
        {
            name: "title",
            label: "Title",
            options: {
                customBodyRender: (value) => value || '-'
            }
        },
        {
            name: "prompt_text",
            label: "Pitch Text",
            options: {
                customBodyRender: (value) => {
                    const text = value || '';
                    return text.length > 40 ? text.substring(0, 40) + '...' : text;
                }
            }
        },
        {
            name: "is_active",
            label: "Status",
            options: {
                display: false
            }
        },
        {
            name: "created_by",
            label: "Created By",
            options: {
                customBodyRender: (value) => value ? `${value.first_name}` : '-'
            }
        },
        {
            name: "modified",
            label: "Modified",
            options: {
                customBodyRender: (value) => {
                    if (!value) return '-';
                    const date = new Date(value);
                    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                }
            }
        },
        {
            name: "action",
            label: "Actions",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = promptData[tableMeta.rowIndex];
                    const id = tableMeta.rowData[0];
                    const title = tableMeta.rowData[1];
                    const isActive = tableMeta.rowData[3];
                    const creatorId = rowData?.created_by?.pk || rowData?.created_by?.id;
                    const isCreator = currentUserId && creatorId && currentUserId === creatorId;
                    
                    return (
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="View">
                                <IconButton
                                    onClick={() => handleOpenPitchViewDrawer(id)}
                                    color="info"
                                    size="small"
                                >
                                    <Visibility />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isCreator ? "Edit" : "Only creator can edit"}>
                                <span>
                                    <IconButton
                                        onClick={() => isCreator && handleOpenPitchDrawer(id)}
                                        color="primary"
                                        size="small"
                                        disabled={!isCreator}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={isCreator ? "Delete" : "Only creator can delete"}>
                                <span>
                                    <IconButton
                                        onClick={() => isCreator && handleDeleteClick(id, title, 'pitch')}
                                        color="error"
                                        size="small"
                                        disabled={!isCreator}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    );
                }
            }
        }
    ];

    // Enrichment Prompts columns (Prompt tab)
    const enrichmentColumns = [
        {
            name: "id",
            label: "ID",
            options: { display: false }
        },
        {
            name: "title",
            label: "Title",
            options: {
                customBodyRender: (value) => value || '-'
            }
        },
        {
            name: "prompt_text",
            label: "Search Criteria",
            options: {
                customBodyRender: (value) => {
                    const text = value || '';
                    return text.length > 40 ? text.substring(0, 40) + '...' : text;
                }
            }
        },
        {
            name: "total_runs",
            label: "Runs",
            options: {
                customBodyRender: (value) => value || 0
            }
        },
        {
            name: "is_active",
            label: "Active",
            options: { display: false }
        },
        {
            name: "created_by",
            label: "Created By",
            options: {
                customBodyRender: (value) => value ? `${value.first_name}` : '-'
            }
        },
        {
            name: "action",
            label: "Actions",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = enrichmentData[tableMeta.rowIndex];
                    const id = tableMeta.rowData[0];
                    const title = tableMeta.rowData[1];
                    const creatorId = rowData?.created_by?.pk || rowData?.created_by?.id;
                    const isCreator = currentUserId && creatorId && currentUserId === creatorId;
                    
                    return (
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="View">
                                <IconButton
                                    onClick={() => handleOpenPromptViewDrawer(id)}
                                    color="info"
                                    size="small"
                                >
                                    <Visibility />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isCreator ? "Edit" : "Only creator can edit"}>
                                <span>
                                    <IconButton
                                        onClick={() => isCreator && handleOpenEnrichmentDrawer(id)}
                                        color="primary"
                                        size="small"
                                        disabled={!isCreator}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={isCreator ? "Delete" : "Only creator can delete"}>
                                <span>
                                    <IconButton
                                        onClick={() => isCreator && handleDeleteClick(id, title, 'enrichment')}
                                        color="error"
                                        size="small"
                                        disabled={!isCreator}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    );
                }
            }
        }
    ];
    
    // Outreach/History columns (Tab 3)
    const historyColumns = [
        {
            name: "id",
            label: "ID",
            options: { display: false }
        },
        {
            name: "title",
            label: "Campaign",
            options: {
                customBodyRender: (value, tableMeta) => {
                    const rowData = historyData[tableMeta.rowIndex];
                    return value || rowData?.enrichment_prompt?.title || '-';
                }
            }
        },
        {
            name: "enrichment_prompt",
            label: "Country",
            options: {
                customBodyRender: (value) => value?.country || '-'
            }
        },
        {
            name: "status",
            label: "Status",
            options: {
                customBodyRender: (value) => {
                    const colorMap = {
                        'pending': 'default',
                        'parsing': 'info',
                        'searching': 'info',
                        'creating': 'info',
                        'generating_pitches': 'warning',
                        'completed': 'success',
                        'failed': 'error',
                        'no_new_companies': 'warning'
                    };
                    return <Chip size="small" label={value} color={colorMap[value] || 'default'} />;
                }
            }
        },
        {
            name: "requested_count",
            label: "Req",
            options: {
                customBodyRender: (value) => value || 0
            }
        },
        {
            name: "actual_count",
            label: "Created",
            options: {
                customBodyRender: (value) => value || 0
            }
        },
        {
            name: "pitches_generated",
            label: "Pitches",
            options: {
                customBodyRender: (value) => value || 0
            }
        },
        {
            name: "success_rate",
            label: "Success %",
            options: {
                customBodyRender: (value, tableMeta) => {
                    const rowData = historyData[tableMeta.rowIndex];
                    const totalCreated = rowData?.actual_count || 0;
                    const at10Percent = rowData?.companies_at_10_percent || 0;
                    
                    if (totalCreated === 0) return '-';
                    
                    const rate = Math.round((at10Percent / totalCreated) * 100);
                    const color = rate >= 50 ? 'success' : rate >= 25 ? 'warning' : 'default';
                    
                    return (
                        <Chip 
                            size="small" 
                            label={`${rate}% (${at10Percent}/${totalCreated})`}
                            color={color}
                            variant="outlined"
                        />
                    );
                }
            }
        },
        {
            name: "triggered_by",
            label: "By",
            options: {
                customBodyRender: (value) => value ? `${value.first_name}` : '-'
            }
        },
        {
            name: "created",
            label: "Date",
            options: {
                customBodyRender: (value) => {
                    if (!value) return '-';
                    const date = new Date(value);
                    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                }
            }
        },
        {
            name: "action",
            label: "Actions",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value, tableMeta) => {
                    const id = tableMeta.rowData[0];
                    return (
                        <IconButton
                            onClick={() => navigate(`/enrichment-progress/${id}`)}
                            color="info"
                            size="small"
                            title="View Details"
                        >
                            <Visibility />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        selectableRows: "none",
        filter: false,
        search: true,
        print: false,
        download: false,
        viewColumns: false,
        serverSide: true,
        count: count,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 50],
        page: currentPage,
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case 'search':
                    debouncedSearch(tableState.searchText);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                default:
                    break;
            }
        }
    };
    
    const enrichmentOptions = {
        selectableRows: "none",
        filter: false,
        search: false,
        print: false,
        download: false,
        viewColumns: false,
        serverSide: true,
        count: enrichmentCount,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 50],
        page: enrichmentPage,
        onTableChange: (action, tableState) => {
            if (action === 'changePage') {
                setEnrichmentPage(tableState.page);
            } else if (action === 'changeRowsPerPage') {
                setRowsPerPage(tableState.rowsPerPage);
            }
        }
    };
    
    const historyOptions = {
        selectableRows: "none",
        filter: false,
        search: false,
        print: false,
        download: false,
        viewColumns: false,
        serverSide: true,
        count: historyCount,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 50],
        page: enrichmentPage,
        onTableChange: (action, tableState) => {
            if (action === 'changePage') {
                setEnrichmentPage(tableState.page);
            } else if (action === 'changeRowsPerPage') {
                setRowsPerPage(tableState.rowsPerPage);
            }
        }
    };

    const renderTable = () => {
        const tableWrapper = (children) => (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ThemeProvider theme={theme}>
                    {children}
                </ThemeProvider>
            </Box>
        );

        // Tab 0: Outreach (campaigns/history) - Consolidated view
        if (mainTab === 0) {
            return (
                <ConsolidatedCampaignTable 
                    historyData={historyData}
                    loading={historyLoading}
                    onNavigate={navigate}
                    onDeleteRun={handleDeleteRun}
                    onDeleteCampaign={handleDeleteCampaign}
                    onViewDetails={handleOpenCampaignDetail}
                    onRerun={handleRerunCampaign}
                    rerunningCampaign={rerunningCampaign}
                    onViewProgress={handleOpenProgressDrawer}
                />
            );
        }
        
        // Tab 1: Prompt (search criteria)
        if (mainTab === 1) {
            return enrichmentLoading && enrichmentData.length === 0 ? (
                <Stack spacing={1} sx={{ py: 2 }}>
                    {[...Array(5)].map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
                    ))}
                </Stack>
            ) : tableWrapper(
                <MUIDataTable
                    data={enrichmentData}
                    columns={enrichmentColumns}
                    options={enrichmentOptions}
                />
            );
        }
        
        // Tab 2: Pitch (templates)
        return isLoading && promptData.length === 0 ? (
            <Stack spacing={1} sx={{ py: 2 }}>
                {[...Array(5)].map((_, index) => (
                    <Skeleton key={index} variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
                ))}
            </Stack>
        ) : tableWrapper(
            <MUIDataTable
                data={promptData}
                columns={columns}
                options={options}
            />
        );
    };

    // Get button label based on tab
    const getAddButtonLabel = () => {
        if (mainTab === 0) return 'Run Campaign';
        if (mainTab === 1) return 'Add Prompt';
        return 'Add Pitch';
    };

    // Get button click handler based on tab
    const handleAddButtonClick = () => {
        if (mainTab === 0) handleOpenOutreachDrawer();
        else if (mainTab === 1) handleOpenEnrichmentDrawer();
        else handleOpenPitchDrawer();
    };

    // Get tab index from value
    const getTabIndex = (value) => {
        if (value === 'outreach') return 0;
        if (value === 'prompt') return 1;
        return 2;
    };

    return (
        <Page title="Outreach | CRM">
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: 'calc(100vh - 64px)',
                overflow: 'hidden',
                backgroundColor: '#fff'
            }}>
                {/* Fixed Header */}
                <Box sx={{ 
                    px: 3, 
                    py: 1.5, 
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                    flexShrink: 0,
                    zIndex: 10
                }}>
                    {/* Title Row */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h5" fontWeight={700}>
                            Outreach
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleAddButtonClick}
                            startIcon={<Icon icon={plusFill} />}
                            style={StyledButton}
                            size="small"
                        >
                            {getAddButtonLabel()}
                        </Button>
                    </Stack>

                    {/* Main Tabs */}
                    <Tabs
                        value={mainTab}
                        onChange={(e, newValue) => handleMainTabChange(newValue)}
                    >
                        <Tab label="Outreach" />
                        <Tab label="Prompt" />
                        <Tab label="Pitch" />
                    </Tabs>
                </Box>

                {/* Table Area */}
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    px: 3, 
                    py: 1.5
                }}>
                    {renderTable()}
                </Box>
            </Box>

            {/* Drawer for Prompt/Pitch forms */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 500, md: 600 } }
                }}
            >
                {drawerType === 'pitch' ? (
                    <PromptForm 
                        id={selectedPromptId} 
                        onClose={handleCloseDrawer}
                        onSuccess={handleRefresh}
                        viewMode={isViewMode}
                    />
                ) : (
                    <EnrichmentPromptForm 
                        id={selectedPromptId} 
                        onClose={handleCloseDrawer}
                        onSuccess={handleRefresh}
                        viewMode={isViewMode}
                    />
                )}
            </Drawer>

            {/* Outreach Drawer - 70% width */}
            <Drawer
                anchor="right"
                open={outreachDrawerOpen}
                onClose={handleCloseOutreachDrawer}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: '70%' } }
                }}
            >
                <OutreachDrawer
                    onClose={handleCloseOutreachDrawer}
                    onSuccess={handleRefresh}
                    countryData={countryData}
                    allEnrichmentPrompts={allEnrichmentPrompts}
                    allPitchTemplates={allPromptTemplates}
                    onOpenProgress={handleOpenProgressDrawer}
                />
            </Drawer>

            {/* Campaign Detail Drawer */}
            <Drawer
                anchor="right"
                open={campaignDetailDrawerOpen}
                onClose={handleCloseCampaignDetail}
                PaperProps={{
                    sx: { 
                        width: isCompanyExpanded 
                            ? { xs: '100%', sm: '85%', md: '80%', lg: '75%' }
                            : { xs: '100%', sm: 450 },
                        transition: 'width 0.3s ease'
                    }
                }}
            >
                <CampaignDetailDrawer
                    open={campaignDetailDrawerOpen}
                    onClose={handleCloseCampaignDetail}
                    campaign={selectedCampaign}
                    onNavigate={navigate}
                    onExpandChange={setIsCompanyExpanded}
                />
            </Drawer>

            {/* Progress Details Drawer */}
            <Drawer
                anchor="right"
                open={progressDrawerOpen}
                onClose={handleCloseProgressDrawer}
                PaperProps={{
                    sx: { 
                        width: isProgressExpanded 
                            ? { xs: '100%', sm: '85%', md: '75%' }
                            : { xs: '100%', sm: '40%', md: '30%' },
                        transition: 'width 0.3s ease'
                    }
                }}
            >
                {selectedRunHistoryId && (
                    <EnrichmentProgress
                        runHistoryIdProp={selectedRunHistoryId}
                        onClose={handleCloseProgressDrawer}
                        isDrawerMode={true}
                        onExpandChange={setIsProgressExpanded}
                    />
                )}
            </Drawer>

            {/* Re-run Confirmation Dialog */}
            <Dialog
                open={rerunConfirmOpen}
                onClose={handleCancelRerun}
                aria-labelledby="rerun-dialog-title"
                aria-describedby="rerun-dialog-description"
            >
                <DialogTitle id="rerun-dialog-title">
                    Re-run Campaign
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="rerun-dialog-description">
                        Are you sure you want to re-run the campaign "{pendingRerunCampaign?.title}"? 
                        This will start a new enrichment run with the same parameters.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRerun}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmRerun} 
                        color="primary" 
                        variant="contained"
                        startIcon={<Replay />}
                    >
                        Re-run
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete {deleteType === 'enrichment' ? 'Prompt' : 'Pitch'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete "{promptToDelete?.title}"? 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Page>
    );
}
